/**
 * VVSection — Step 8 V&V tab content. Auto-fetches existing
 * recommendations on mount when status is `completed` so the table
 * is the immediate landing UI (no "View Recommendations" click
 * required). The VVGenerationCard only renders during idle /
 * generating / failed states; once data is loaded it disappears
 * entirely so the table owns the tab.
 *
 * Backend ships two canonical shapes (one per analysis mode):
 *   • individual    → recommendations[i]                                 = flat VVRecord
 *   • hierarchical  → recommendations[i].recommendation                  = VVRecord
 *                     recommendations[i].{rationale,alternative_methods} = siblings
 * The normalizer below unwraps the hierarchical wrapper when present
 * so the table schema reads field keys uniformly off each row.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import VVGenerationCard from '../../components/vv/VVGenerationCard';
import VVTable from './VVTable';
import { getVVRecommendations, getVVRecommendationsStatus } from '../../../../api/vvRecommendations';
import { type VVRecord } from './vvFieldSchema';
import styles from './Step8Tables.module.css';

const VVGenerationCardAny = VVGenerationCard as unknown as React.ComponentType<any>;

/** Per-entry shape — either flat (individual mode) or wrapped
 *  (hierarchical mode). The normalizer in `rows` collapses both into
 *  flat VVRecord rows for the table. */
type VVEntry =
  | VVRecord
  | { recommendation: VVRecord; rationale?: string; alternative_methods?: string[] };

interface VVApiPayload {
  recommendations?: VVEntry[];
  total_requirements?: number;
  cal_distribution?: Record<string, number>;
  validation_strategy?: string;
  resource_recommendations?: string;
  summary?: string;
  [key: string]: unknown;
}

export interface VVSectionProps {
  threadId: string | null | undefined;
  analysisMode?: string | null;
  /** Current V&V payload (null until fetched / generated). */
  data: VVApiPayload | null;
  /** Set by the consumer when generation completes (legacy
   *  VVGenerationCard fires onGenerationComplete with the payload). */
  onDataChange: (payload: VVApiPayload | null) => void;
  /** Disable generation while other agents are in flight. */
  disabled?: boolean;
}

export default function VVSection({
  threadId,
  analysisMode,
  data,
  onDataChange,
  disabled,
}: VVSectionProps) {
  // Auto-fetch on first mount when status is already `completed`.
  // Without this the user would land on the V&V tab and see the
  // legacy "View Recommendations" button — one extra click for data
  // that's already on the server. Ref guards against double-fetch
  // when the consumer re-renders mid-load.
  //
  // `initializing` gates the render: while the initial status check is
  // in flight, neither the GenerationCard nor the (empty) table
  // appears. Without this gate the GenerationCard flashes for ~200ms
  // on every refresh of a completed V&V tab.
  const autoFetchedRef = useRef(false);
  const [initializing, setInitializing] = useState(() => !data);
  useEffect(() => {
    if (autoFetchedRef.current) return;
    if (!threadId) {
      setInitializing(false);
      return;
    }
    if (data) {
      setInitializing(false);
      return;
    }
    autoFetchedRef.current = true;
    (async () => {
      try {
        const status = (await getVVRecommendationsStatus(threadId as string)) as unknown as
          | { status?: string }
          | null;
        if (status?.status === 'completed') {
          const payload = (await getVVRecommendations(threadId as string)) as unknown as VVApiPayload;
          if (payload) onDataChange(payload);
        }
      } catch {
        // Silently fall through — the GenerationCard handles error
        // surfacing for the user; this auto-fetch is best-effort.
      } finally {
        setInitializing(false);
      }
    })();
  }, [threadId, data, onDataChange]);

  // Normalize both backend shapes:
  //   • hierarchical → entry = { recommendation, rationale, alternative_methods }
  //                    → unwrap + merge siblings onto the row
  //   • individual   → entry IS the VVRecord → pass through
  // Detection: entry has a non-null `recommendation` object property.
  const rows: VVRecord[] = useMemo(() => {
    const list = data?.recommendations ?? [];
    return list.map((entry) => {
      const wrapper = entry as { recommendation?: VVRecord; rationale?: string; alternative_methods?: string[] };
      if (wrapper.recommendation && typeof wrapper.recommendation === 'object') {
        return {
          ...wrapper.recommendation,
          rationale: wrapper.rationale,
          alternative_methods: wrapper.alternative_methods,
        };
      }
      return entry as VVRecord;
    });
  }, [data]);

  // Initial status check still in flight — render nothing so the
  // GenerationCard doesn't flash before auto-fetch resolves.
  if (initializing) {
    return <div className={styles.section} />;
  }

  // When the table has rows, the generation card disappears — table
  // owns the tab. Otherwise show the card so user can generate / see
  // progress / retry on failure.
  if (rows.length > 0) {
    return (
      <div className={styles.section}>
        <VVTable recommendations={rows} />
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <VVGenerationCardAny
        threadId={threadId}
        onGenerationComplete={onDataChange}
        analysisMode={analysisMode ?? 'individual'}
        disabled={disabled}
      />
    </div>
  );
}
